/**
 * inkMatrixField.ts
 * 「圆形呼吸矩阵」的**排布 + 随机场参数**：纯函数，模块加载时算一次，运行时零计算。
 *
 * 为什么不每帧用 rAF 算：
 *   动画仍然全部交给 CSS（transform / opacity 由 compositor 合成），主线程一帧都不参与 ——
 *   95 个点的相位 / 周期 / 幅度在模块加载时算好、写进内联 CSS 变量即可。
 *   每帧用 JS 改 95 个元素的 style 会在写作时抢主线程，装饰动画不该付这个代价。
 *
 * 为什么是"随机场"而不是"逐点随机"：
 *   逐点白噪声 → 每点各闪各的，读起来像雪花噪点（廉价，也与水墨气质不符）；
 *   二维值噪声（空间连续）→ 相邻点相位接近 ⇒ 出现**成片的亮斑**，亮斑随时间流动、
 *   变形、在随机位置亮起 —— 一片"活着的能量场"，这才是要的 AI 感。
 *
 * 为什么不再是"同心波"：
 *   旧版把相位按环分配（中心最领先、最外环滞后 0.72s），且**同环所有点完全一致** ⇒
 *   读起来是一圈一圈规律向外推的波纹（用户实测："太平常了"）。现在：
 *     相位 = 弱径向倾向（仅 0.42s，留一点向外的推演感）+ 强噪声抖动（±0.85s）；
 *     尺寸层与亮度层是**两个独立随机相位、互不整除的周期** ⇒ 合成波形不显周期。
 */

/** 矩阵外接方框边长（px）；必须与 ai-panel.css 的 --ink-matrix-size / --ink-matrix-half 一致 */
export const MATRIX_SIZE = 96;
/** 同心环数（环 0 = 正中心点） */
export const RING_COUNT = 5;
/** 相邻环的半径间距（px）：5 环 × 8 = 40，96px 方框半径 48 留出边缘余量 */
export const RING_STEP = 8;

/** 中心点直径（px） / 最外环直径（px） */
const DOT_MAX = 5.6;
const DOT_MIN = 1.9;
/** 径向衰减指数：0.5 = 开方衰减（中心一片大点、边缘收得柔和） */
const FALLOFF_EXP = 0.5;
/** 静态径向透明度（%）：中心 100 → 边缘 50（喂给 color-mix，**不参与动画**） */
const ALPHA_CENTER = 100;
const ALPHA_EDGE = 50;
/** 印心：环距 ≤ 该值的点用主色（--primary-color） */
const CORE_RING = 1;

/** 随机场种子：固定值 ⇒ 每次打开都是同一套"随机"（可复现、可单测，不会每次闪烁换一套） */
export const FIELD_SEED = 20260208;

/** 尺寸层周期区间（秒） */
const DUR_A_MIN = 2.5;
const DUR_A_MAX = 4.3;
/** 亮度层周期区间（秒）：与尺寸层不同频 ⇒ 两层叠加不显周期 */
const DUR_B_MIN = 1.7;
const DUR_B_MAX = 2.8;
/** 相位抖动幅度（秒）：远大于旧版按环给的 0.72s ⇒ 同环不再同步 */
const PHASE_JITTER = 0.85;
/** 保留的"中心略领先"倾向（秒）：弱到读不出同心波，只留一点向外的推演感 */
const PHASE_RADIAL = 0.42;
/** 负延迟基址：保证 animation-delay 恒为负 ⇒ 所有点一进画面就已在动（没有"起跑"） */
const DELAY_BASE = 1.2;
/** 幅度倍率区间（乘在关键帧上，见 ai-panel.css 的 --ink-amp-a / --ink-amp-b） */
const AMP_A_MIN = 0.9;
const AMP_A_MAX = 1.18;
const AMP_B_MIN = 0.85;
const AMP_B_MAX = 1;
/** 噪声空间频率（1/px）：值越小 → 亮斑越大（A 组大斑 / B 组小斑，叠出层次） */
const FREQ_A = 0.055;
const FREQ_B = 0.1;
/** 脉冲点个数：极少 —— 偶尔闪一下的"数据脉冲"；多了就成闪烁噪音 */
const BLIP_COUNT = 4;
/** 脉冲周期区间（秒）：配合 alternate，实际闪动间隔约为它的 2 倍 */
const BLIP_DUR_MIN = 3.4;
const BLIP_DUR_MAX = 5.2;

export interface MatrixDotSpec {
  /** 静态定位（px，相对矩阵方框左上角） */
  left: number;
  top: number;
  /** 静态点径（px） */
  size: number;
  /** 静态径向透明度（%）：喂给 background 的 color-mix；动画只动 opacity */
  alphaPct: number;
  /** 环序（0 = 中心）；"同环相位不再一致"是本次改动的关键 */
  ring: number;
  /** 印心点（环距 ≤ CORE_RING）：唯一的主色落点 */
  core: boolean;
  /** 脉冲点：亮度层换成短促尖峰曲线 */
  blip: boolean;
  /** 尺寸层：幅度倍率 / 周期 / 负延迟 */
  ampA: number;
  durA: string;
  delayA: string;
  /** 亮度层：同上，与尺寸层相互独立 */
  ampB: number;
  durB: string;
  delayB: string;
}

/** mulberry32：32 位确定性 PRNG —— 同一 seed 永远同一序列（视觉可复现、可单测） */
export function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** 格点哈希 → [0,1)：整数混合，保证同一 (ix, iy, seed) 恒定 */
function hash2(ix: number, iy: number, seed: number): number {
  let h = Math.imul(ix, 374761393) + Math.imul(iy, 668265263) + Math.imul(seed, 2246822519);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

const smoothstep = (t: number): number => t * t * (3 - 2 * t);

/** 二维值噪声 ∈ [0,1)：格点取随机值 + smoothstep 双线性插值 ⇒ **空间连续** */
export function valueNoise2D(x: number, y: number, seed: number): number {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = smoothstep(x - ix);
  const fy = smoothstep(y - iy);
  const n00 = hash2(ix, iy, seed);
  const n10 = hash2(ix + 1, iy, seed);
  const n01 = hash2(ix, iy + 1, seed);
  const n11 = hash2(ix + 1, iy + 1, seed);
  const top = n00 + (n10 - n00) * fx;
  const bottom = n01 + (n11 - n01) * fx;
  return top + (bottom - top) * fy;
}

/** 第 r 环上的点数：按周长均布，间距 ≈ RING_STEP ⇒ n ≈ 2πr */
export function dotsInRing(r: number): number {
  return r === 0 ? 1 : Math.max(6, Math.round(2 * Math.PI * r));
}

export function buildInkMatrixDots(): MatrixDotSpec[] {
  const out: MatrixDotSpec[] = [];
  const blipScore: number[] = [];
  const rng = makeRng(FIELD_SEED);
  const centerPx = MATRIX_SIZE / 2;

  for (let r = 0; r <= RING_COUNT; r += 1) {
    const radius = r * RING_STEP;
    const n = dotsInRing(r);
    // 交替环错开半格：相邻环的点不排在一条射线上，堆叠得更自然
    const angleOffset = (r % 2) * (Math.PI / n);
    const t = r / RING_COUNT;                       // 0 = 中心，1 = 最外环
    const tFall = Math.pow(t, FALLOFF_EXP);
    const size = DOT_MAX - (DOT_MAX - DOT_MIN) * tFall;
    const alphaPct = ALPHA_CENTER - (ALPHA_CENTER - ALPHA_EDGE) * tFall;

    for (let i = 0; i < n; i += 1) {
      const angle = angleOffset + (i / n) * Math.PI * 2;
      const cx = centerPx + radius * Math.cos(angle);
      const cy = centerPx + radius * Math.sin(angle);
      // ── 随机场：四张不同种子/频率的噪声（相位 A、相位 B、周期、幅度）──
      const nA = valueNoise2D(cx * FREQ_A, cy * FREQ_A, FIELD_SEED + 11);
      const nB = valueNoise2D(cx * FREQ_B, cy * FREQ_B, FIELD_SEED + 23);
      const nC = valueNoise2D(cx * FREQ_A + 37, cy * FREQ_A + 37, FIELD_SEED + 31);
      const nD = valueNoise2D(cx * FREQ_B + 71, cy * FREQ_B + 71, FIELD_SEED + 41);
      // 相位：弱径向倾向 + 强噪声抖动；二者相加后仍保持"空间成片"
      const phaseA = (1 - t) * PHASE_RADIAL + (nA - 0.5) * 2 * PHASE_JITTER;
      const phaseB = (nB - 0.5) * 2 * PHASE_JITTER;
      out.push({
        left: Number((cx - size / 2).toFixed(2)),
        top: Number((cy - size / 2).toFixed(2)),
        size: Number(size.toFixed(2)),
        alphaPct: Number(alphaPct.toFixed(1)),
        ring: r,
        core: r <= CORE_RING,
        blip: false,                                  // 下面按抽签统一标记
        ampA: Number((AMP_A_MIN + nC * (AMP_A_MAX - AMP_A_MIN)).toFixed(3)),
        durA: (DUR_A_MIN + nC * (DUR_A_MAX - DUR_A_MIN)).toFixed(2) + 's',
        delayA: (-(phaseA + DELAY_BASE)).toFixed(2) + 's',
        ampB: Number((AMP_B_MIN + nD * (AMP_B_MAX - AMP_B_MIN)).toFixed(3)),
        durB: (DUR_B_MIN + nD * (DUR_B_MAX - DUR_B_MIN)).toFixed(2) + 's',
        delayB: (-(phaseB + DELAY_BASE + 0.6)).toFixed(2) + 's',
      });
      blipScore.push(rng());
    }
  }

  // 脉冲点：抽签值最高的 BLIP_COUNT 个（跳过中心点 —— 核心区闪起来太吵）
  const picked = blipScore
    .map((score, idx) => ({ score, idx }))
    .filter((e) => out[e.idx].ring >= 1)
    .sort((a, b) => b.score - a.score)
    .slice(0, BLIP_COUNT);
  for (const e of picked) {
    out[e.idx].blip = true;
    // 脉冲点的"亮度层"周期改用长周期 ⇒ 偶尔闪一下
    out[e.idx].durB = (BLIP_DUR_MIN + e.score * (BLIP_DUR_MAX - BLIP_DUR_MIN)).toFixed(2) + 's';
  }
  return out;
}
